import { ChevronLeft, ChevronRight, Trophy, Heart, Lightbulb, Target, CheckCircle2, Circle, Sparkles, Gauge } from "lucide-react";
import { useRhythm } from "@/context/RhythmStateContext";
import { buildScoreboard, domains, weekRangeLabel, shiftWeek, weekKey, isoLocal, emptyReview } from "@/lib/rhythm";
import { scoreTone, statusStyles } from "@/lib/central";
import { PageHeader, SectionCard, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

const WeekNav = ({ week, setWeek }) => {
  const thisWeek = weekKey(isoLocal(new Date()));
  return (
    <div className="flex items-center gap-1 rounded-[10px] border border-[color:var(--border-subtle)] bg-white p-0.5 w-max">
      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setWeek(shiftWeek(week, -1))} data-testid="week-prev" aria-label="Previous week"><ChevronLeft className="h-4 w-4" /></Button>
      <button onClick={() => setWeek(thisWeek)} className={cn("px-3 text-sm min-w-[190px] text-center transition-colors hover:text-[var(--green)]", week === thisWeek ? "text-[var(--text)]" : "text-[var(--gold)]")} data-testid="week-label">{week === thisWeek ? "This week · " : ""}{weekRangeLabel(week)}</button>
      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setWeek(shiftWeek(week, 1))} data-testid="week-next" aria-label="Next week"><ChevronRight className="h-4 w-4" /></Button>
    </div>
  );
};

const ScoreChip = ({ label, value, color }) => {
  const tone = value == null ? null : statusStyles[scoreTone(value)];
  return (
    <div className="rounded-[12px] border border-[color:var(--border-subtle)] bg-white px-3 py-2.5">
      <div className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ backgroundColor: color }} /><span className="text-[11px] font-medium text-[var(--text-muted)] truncate">{label}</span></div>
      <div className="mt-1 font-serif-display text-2xl tabular-nums" style={{ color: tone ? tone.color : "var(--text-faint)" }}>{value == null ? "—" : value}</div>
    </div>
  );
};

export default function WeeklyReview() {
  const { loading, week, setWeek, fin, life, wellness, pillarStore, review, plan, updateReview, updatePlan } = useRhythm();
  if (loading) return <div className="space-y-4"><Skeleton className="h-28 rounded-[14px]" /><Skeleton className="h-96 rounded-[14px]" /></div>;

  const r = { ...emptyReview(), ...(review || {}) };
  const board = buildScoreboard(fin, life, wellness, pillarStore);
  const big3 = plan?.big3 || [];

  const setRating = (k, v) => updateReview((prev) => ({ ...prev, ratings: { ...emptyReview().ratings, ...(prev.ratings || {}), [k]: v } }));
  const setArr = (key, i, val) => updateReview((prev) => { const a = [...(prev[key] || emptyReview()[key])]; a[i] = val; return { ...prev, [key]: a }; });
  const setField = (k, v) => updateReview((prev) => ({ ...prev, [k]: v }));
  const gradeBig3 = (i) => updatePlan((prev) => { const a = [...(prev.big3 || [])]; if (a[i]) a[i] = { ...a[i], done: !a[i].done }; return { ...prev, big3: a }; });

  const wonCount = big3.filter((b) => b.done).length;

  return (
    <div>
      <PageHeader title="Weekly Review" subtitle="Close the loop on everything. What got done, what you learned, how you're really doing." testid="weekly-review-header" actions={<WeekNav week={week} setWeek={setWeek} />} />

      {/* Scoreboard */}
      <SectionCard className="mb-4" testid="review-scoreboard"
        title={<span className="flex items-center gap-2"><Gauge className="h-4 w-4 text-[var(--gold)]" />Whole-life scoreboard</span>}
        subtitle="Pulled live from all your command centers">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-3 shrink-0">
            <div className="grid place-items-center h-20 w-20 rounded-full border-4 border-[color:rgba(31,90,73,0.15)]">
              <div className="text-center"><div className="font-serif-display text-2xl text-[var(--text)] tabular-nums" data-testid="review-index">{board.index == null ? "—" : board.index}</div><div className="text-[9px] uppercase tracking-wide text-[var(--text-muted)]">index</div></div>
            </div>
            <div className="text-sm text-[var(--text-muted)] max-w-[180px]">Your whole-life index this week. Aim to move it up 1–2 points every review.</div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 flex-1">
            {board.pillars.map((p) => <ScoreChip key={p.id} label={p.short} value={p.score} color={p.color} />)}
          </div>
        </div>
      </SectionCard>

      {/* Big 3 accountability */}
      <SectionCard className="mb-4" testid="review-big3"
        title={<span className="flex items-center gap-2"><Target className="h-4 w-4 text-[var(--gold)]" />Did you win your Big 3?</span>}
        subtitle={big3.length ? `${wonCount} of ${big3.length} won this week` : undefined}>
        {big3.length && big3.some((b) => b.text) ? (
          <div className="space-y-2">
            {big3.filter((b) => b.text).map((b, i) => (
              <button key={i} onClick={() => gradeBig3(big3.indexOf(b))} data-testid={`review-big3-${i}`}
                className={cn("w-full text-left flex items-center gap-3 rounded-[10px] border px-3 py-2.5 transition-colors", b.done ? "border-[color:rgba(31,115,85,0.4)] bg-[color:rgba(31,115,85,0.06)]" : "border-[color:var(--border-subtle)] bg-white hover:border-[color:var(--border-strong)]")}>
                {b.done ? <CheckCircle2 className="h-5 w-5 text-[var(--green)] shrink-0" /> : <Circle className="h-5 w-5 text-[var(--text-faint)] shrink-0" />}
                <span className={cn("text-sm", b.done ? "line-through text-[var(--text-faint)]" : "text-[var(--text)]")}>{b.text}</span>
              </button>
            ))}
          </div>
        ) : (
          <EmptyState icon={Target} title="No Big 3 set for this week" message="Set your Big 3 in Weekly Plan first — then grade them here at week's end. That loop is where the compounding happens." />
        )}
      </SectionCard>

      {/* Ratings */}
      <SectionCard className="mb-4" title="Rate your week" subtitle="Honest self-assessment, 1–10" testid="review-ratings">
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1.5"><span className="text-sm font-medium text-[var(--text)]">Overall week</span><span className="text-sm tabular-nums text-[var(--green)] font-medium">{r.ratings.overall}</span></div>
          <Slider value={[r.ratings.overall]} min={1} max={10} step={1} onValueChange={(v) => setRating("overall", v[0])} data-testid="rating-overall" />
        </div>
        <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
          {domains.map((d) => (
            <div key={d.id}>
              <div className="flex items-center justify-between mb-1.5"><span className="text-[13px] text-[var(--text-muted)] flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: d.color }} />{d.name}</span><span className="text-[13px] tabular-nums text-[var(--text)]">{r.ratings[d.id]}</span></div>
              <Slider value={[r.ratings[d.id]]} min={1} max={10} step={1} onValueChange={(v) => setRating(d.id, v[0])} data-testid={`rating-${d.id}`} />
            </div>
          ))}
        </div>
      </SectionCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <SectionCard title={<span className="flex items-center gap-2"><Trophy className="h-4 w-4 text-[var(--gold)]" />Wins</span>} subtitle="Name three, however small" testid="review-wins">
          <div className="space-y-2">{r.wins.map((w, i) => (<Input key={i} value={w} onChange={(e) => setArr("wins", i, e.target.value)} placeholder={`Win ${i + 1}`} data-testid={`win-${i}`} className="bg-white border-[color:var(--border-subtle)]" />))}</div>
        </SectionCard>
        <SectionCard title={<span className="flex items-center gap-2"><Heart className="h-4 w-4 text-[var(--gold)]" />Gratitude</span>} subtitle="What are you thankful for?" testid="review-gratitude">
          <div className="space-y-2">{r.gratitude.map((w, i) => (<Input key={i} value={w} onChange={(e) => setArr("gratitude", i, e.target.value)} placeholder={`Grateful for…`} data-testid={`gratitude-${i}`} className="bg-white border-[color:var(--border-subtle)]" />))}</div>
        </SectionCard>
      </div>

      <SectionCard className="mb-4" title={<span className="flex items-center gap-2"><Lightbulb className="h-4 w-4 text-[var(--gold)]" />Lessons &amp; what to change</span>} subtitle="Turn friction into next week's edge" testid="review-lessons">
        <div className="space-y-2">{r.lessons.map((w, i) => (<Input key={i} value={w} onChange={(e) => setArr("lessons", i, e.target.value)} placeholder={`Lesson ${i + 1}`} data-testid={`lesson-${i}`} className="bg-white border-[color:var(--border-subtle)]" />))}</div>
      </SectionCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SectionCard title="Open loops" subtitle="Anything nagging or unfinished to capture" testid="review-openloops">
          <Textarea value={r.openLoops} onChange={(e) => setField("openLoops", e.target.value)} placeholder="Brain-dump anything on your mind so it's out of your head…" data-testid="review-openloops-input" className="min-h-[110px] bg-white border-[color:var(--border-subtle)]" />
        </SectionCard>
        <SectionCard title="Reflections" subtitle="Notes to your future self" testid="review-notes">
          <Textarea value={r.notes} onChange={(e) => setField("notes", e.target.value)} placeholder="How did this week feel? What is God teaching you? What matters most next week?" data-testid="review-notes-input" className="min-h-[110px] bg-white border-[color:var(--border-subtle)]" />
        </SectionCard>
      </div>

      <div className="mt-4 rounded-[var(--radius-card)] border border-[color:rgba(198,139,60,0.3)] bg-[color:rgba(198,139,60,0.08)] p-4 flex items-center gap-2 text-sm text-[var(--text)]" data-testid="review-footer">
        <Sparkles className="h-4 w-4 text-[var(--gold)] shrink-0" />Reviewed and honest? Head to <span className="font-medium">Weekly Plan</span> and set next week's Big 3 while it's fresh — that's the habit that compounds.
      </div>
    </div>
  );
}
